import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { wsService } from '@/lib/websocket';
import { useAuth } from './AuthContext';

interface WebSocketContextType {
  isConnected: boolean;
  activeUsers: number;
  userList: string[];
}

const WebSocketContext = createContext<WebSocketContextType>({
  isConnected: false,
  activeUsers: 0,
  userList: [],
});

export function useWebSocket() {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within WebSocketProvider');
  }
  return context;
}

interface WebSocketProviderProps {
  children: ReactNode;
}

export function WebSocketProvider({ children }: WebSocketProviderProps) {
  const { user } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const [activeUsers, setActiveUsers] = useState(0);
  const [userList, setUserList] = useState<string[]>([]);

  useEffect(() => {
    let countSubscription: any = null;
    let listSubscription: any = null;
    let registered = false;

    // 사용자 정보가 없으면 연결하지 않음
    if (!user?.memberNickname) {
      return;
    }

    // WebSocket 연결
    wsService.connect();

    // 연결 상태 확인 및 구독 (연결 완료 후)
    const checkConnectionTimer = setInterval(() => {
      const connected = wsService.isConnected();
      setIsConnected(connected);

      // 연결되었고 아직 구독하지 않았다면 구독 및 등록
      if (connected && !countSubscription) {

        // 접속자 수 구독
        countSubscription = wsService.subscribeToUserCount((count) => {
          setActiveUsers(count);
        });

        // 접속자 목록 구독
        listSubscription = wsService.subscribeToUserList((users) => {
          setUserList(users);
        });

        // 사용자 등록 (닉네임 전송) - 구독 후 약간의 지연
        if (!registered) {
          setTimeout(() => {
            wsService.registerUser(user.memberNickname);
            registered = true;
          }, 1000);
        }

        clearInterval(checkConnectionTimer);
      }
    }, 500);

    // 정리
    return () => {
      clearInterval(checkConnectionTimer);
      if (countSubscription) {
        countSubscription.unsubscribe();
      }
      if (listSubscription) {
        listSubscription.unsubscribe();
      }
      wsService.disconnect();
    };
  }, [user]);

  return (
    <WebSocketContext.Provider value={{ isConnected, activeUsers, userList }}>
      {children}
    </WebSocketContext.Provider>
  );
}
